/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, MenuItem } from '@material-ui/core';
import { GeneralModal } from '.';
import { ButtonWithLoadingIndicator, CancelButton, ControlledDateInput, ControlledInput, PrimaryTextButton } from '../components-atoms';
import { ITheme } from '../constants/theme';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { makeStyles } from '@material-ui/styles';
import DateFnsUtils from '@date-io/date-fns';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { clearPromotionCodeError, createPromotionCode, generatePromoCode, createErrorExceptions } from '../redux/actions/promotionCodes';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type IUseType = 'SingleUse' | 'MultiUse' | 'OncePerCustomer';
type DiscountType = 'Percent' | 'Value';
type AddPromotionCodeType = {
  Code: string,
  DiscountType: DiscountType;
  Amount: string;
  MinimumSpend: string;
  UseType: IUseType;
  StartDate: MaterialUiPickersDate;
  Expires: MaterialUiPickersDate;
};
interface IProps {
  visible: boolean;
  onClose: () => void;
}

export const AddPromotionCodeModal = ({ visible, onClose }: IProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { handleSubmit, formState: { isValid, errors }, reset, watch, setValue, control, setError, clearErrors } = useForm<any>({ reValidateMode: 'onBlur', mode: 'onBlur' });
  const [discountType, amount, minimumSpend, code] = useWatch({ control, name: ['DiscountType', 'Amount', 'MinimumSpend', 'Code'] });
  const [generatedCode, error, loading, createErrorMessage]: [string, boolean, boolean, string] = useSelector((state: any) => [
    state.PromotionCodesSymbolsReducer.generatedCode,
    state.CreatePromotionCodesSymbolsReducer.error,
    state.CreatePromotionCodesSymbolsReducer.loading,
    state.CreatePromotionCodesSymbolsReducer.message
  ]);
  const [amountFocused, setAmountFocused] = useState<boolean>(false);
  const [minimumSpendFocused, setMinimumSpendFocused] = useState<boolean>(false);
  const isPercent = discountType === 'Percent';
  const minSpendRequired = !isPercent;
  const generateRandomCode = (): void => {
    dispatch(generatePromoCode());
  };
  const onSubmit: SubmitHandler<AddPromotionCodeType> = data => {
    // Format dates as ISO date strings (YYYY-MM-DD) without time/timezone
    const formatDateOnly = (date: MaterialUiPickersDate) => {
      const d = date ? new Date(date) : new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    dispatch(createPromotionCode({
      Code: data.Code,
      Type: `${data.UseType}${data.DiscountType}`,
      Amount: data.DiscountType === 'Value' ? (+data.Amount) * 100 : null,
      Percentage: data.DiscountType === 'Percent' ? +data.Amount : null,
      PromoCode: data.Code,
      Expires: formatDateOnly(data.Expires),
      StartDate: formatDateOnly(data.StartDate),
      MinimumSpend: data.MinimumSpend ? +data.MinimumSpend * 100 : null
    }, () => {
      onClose();
      reset();
    }));
  };
  
  useEffect(() => {
    setValue('Code', generatedCode, { shouldValidate: true, shouldDirty: true });
  }, [generatedCode, setValue]);
  
  useEffect(() => {
    if (error) {
      dispatch(clearPromotionCodeError());
    }
  }, [code]);

  const onDiscountTypeChange = (e) => {
    setValue("DiscountType", e.target.value, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("Amount", "", {
      shouldDirty: true,
      shouldValidate: false,
    });
    setValue("MinimumSpend", "", {
      shouldDirty: true,
      shouldValidate: false,
    });

    clearErrors('MinimumSpend');
  };

  const isMinSpendValid = (val: number) => (val ? +amount <= +val : true);
  const getMinSpendValidationText = (val: number) => {
    const minSpendLowerThanAmount = isMinSpendValid(val);
    
    if (!minSpendRequired || minSpendLowerThanAmount) {
      return true;
    }

    return "A minimum spend amount equal to or greater than the discount amount is required for this discount type.";
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <GeneralModal
        title="Add Promotion Code"
        visible={visible}
        onClose={() => { reset(); onClose(); }}
      >
        <form onSubmit={handleSubmit(onSubmit)} >
          <GeneralModal.ContentArea>
            <fieldset className={classes.fieldset}>
              <Grid container spacing={3}>
                <Grid container item xs={8}>
                  <ControlledInput
                    placeholder="Promotion Code"
                    label="Promotion Code"
                    name="Code"
                    error={(error && !createErrorExceptions.includes(createErrorMessage)) || errors['Code']}
                    helperText={(error && !createErrorExceptions.includes(createErrorMessage)) ? 'This code is already taken' : (errors['Code'] && errors['Code'].type === 'maxLength' ? errors['Code'].message : '')}
                    rules={{
                      required: true,
                      maxLength: 12
                    }}
                    control={control}
                    InputLabelProps={{
                      shrink: !!watch('Code')
                    }}
                    onChange={(e) => {
                      if (e.target.value.length > 12) {
                        setError('Code', { type: 'maxLength', message: 'Max length for promo code is 12 characters' });
                        return;
                      }
                      setValue('Code', e.target.value, { shouldDirty: true, shouldValidate: true });
                    }}
                  />
                </Grid>
                <Grid container item xs={4} justifyContent="flex-end" alignItems="center">
                  <PrimaryTextButton data-automation="generate-code-button" onClick={() => generateRandomCode()} title="Generate Code" />
                </Grid>
              </Grid>
              <ControlledInput
                select
                placeholder="Discount Type"
                label="Discount Type"
                name="DiscountType"
                SelectProps={{
                  MenuProps: {
                    style: {
                      marginTop: 55
                    }
                  }
                }}
                rules={{ required: true }}
                control={control}
                defaultValue=""
                onChange={onDiscountTypeChange}
              >
                <MenuItem style={{ height: 36 }} value={'Percent'}>Percentage</MenuItem>
                <MenuItem style={{ height: 36 }} value={'Value'}>Fixed Amount</MenuItem>
              </ControlledInput>
              <ControlledInput
                placeholder={`Discount ${isPercent ? 'Percentage' : 'Amount'}`}
                label={`Discount ${isPercent ? 'Percentage' : 'Amount'}`}
                type='text'
                name="Amount"
                onFocus={() => setAmountFocused(true)}
                onBlur={() => setAmountFocused(false)}
                InputProps={
                  (amount || amountFocused) && (isPercent
                    ? {
                      endAdornment:
                        <span className={classes.suffix}>
                          %
                        </span>,
                    } :
                    {
                      startAdornment:
                        <span className={classes.prefix}>
{t('currencySymbol')}
                        </span>,
                    })}
                control={control}
                rules={{
                  required: true,
                  min: 0,
                  max: isPercent ? 100 : undefined,
                  validate: val => isPercent || (+minimumSpend ? +val <= +minimumSpend : true)
                }}
                onChange={(e) => {
                  if (e.target.value !== ' ' && (Number.isNaN(+e.target.value) || +e.target.value <= 0)) {
                    return;
                  }
                  setValue('Amount', e.target.value, { shouldDirty: true, shouldValidate: true });
                }}
                helperText={errors['Amount'] && errors['Amount'].type === 'max' && 'Discount percentage cannot be greater than 100%'}
                disabled={!!!discountType}
              />
              <ControlledInput
                placeholder={`Min. Spend Required ${!minSpendRequired ? " (Opt.)" : ""}`}
                label={`Min. Spend Required ${!minSpendRequired ? "(Opt.)" : ""}`}
                type="number"
                name="MinimumSpend"
                rules={{
                  min: 1,
                  required: minSpendRequired,
                  validate: (val: number) => getMinSpendValidationText(val),
                }}
                InputProps={{
                  startAdornment: (minimumSpend || minimumSpendFocused) && (
<span className={classes.prefix}>{t('currencySymbol')}</span>
                  ),
                }}
                onFocus={() => setMinimumSpendFocused(true)}
                onBlur={() => setMinimumSpendFocused(false)}
                control={control}
                onChange={(e) => {
                  if (
                    e.target.value !== "" &&
                    (Number.isNaN(+e.target.value) || +e.target.value <= 0)
                  ) {
                    return;
                  }
                  setValue("MinimumSpend", e.target.value, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }}
                helperText={
                  minSpendRequired && errors["MinimumSpend"]
                    ? errors["MinimumSpend"].message
                    : ""
                }
              />

              <ControlledInput
                select
                placeholder="Use Type"
                label="Use Type"
                name="UseType"
                defaultValue=""
                SelectProps={{
                  MenuProps: {
                    style: {
                      marginTop: 53
                    }
                  }
                }}
                rules={{ required: true }}
                control={control}
              >
                <MenuItem value={'SingleUse'}>Single Use</MenuItem>
                <MenuItem value={'MultiUse'}>Multiple Use</MenuItem>
                <MenuItem value={'OncePerCustomer'}>Once Per Customer</MenuItem>
              </ControlledInput>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <ControlledDateInput
                    placeholder="DD/MM/YYYY"
                    label="Start Date"
                    name="StartDate"
                    onError={(error, value) => {
                      if (!error || errors['StartDate']) {
                        return;
                      }
                      setError('StartDate', { type: 'valueAsDate', message: error.toString() });

                    }}
                    defaultValue={null}
                    maxDateMessage="Please select a date prior to 2100"
                    rules={{
                      required: true,
                    }}
                    control={control}
                    onChange={date => {
                      setValue('StartDate', date, { shouldValidate: true, shouldDirty: true });
                    }}
                    value={watch('StartDate')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ControlledDateInput
                    placeholder="DD/MM/YYYY"
                    label="End Date"
                    name="Expires"
                    onError={(error) => {
                      if (!error || errors['Expires']) {
                        return;
                      }
                      setError('Expires', { type: 'valueAsDate', message: error.toString() });

                    }}
                    defaultValue={null}
                    rules={{
                      required: true,
                    }}
                    minDate={watch('StartDate') || new Date()}
                    minDateMessage={watch('StartDate') ? 'End date should be after the start date' : 'End date cannot be in the past'}
                    maxDateMessage="Please select a date prior to 2100"
                    control={control}
                    onChange={date => setValue('Expires', date, { shouldValidate: true, shouldDirty: true })}
                    value={watch('Expires')}
                  />
                </Grid>
              </Grid>
            </fieldset>
          </GeneralModal.ContentArea>
          <GeneralModal.ButtonArea>
            <CancelButton onClick={() => { reset(); onClose(); }} />
            <ButtonWithLoadingIndicator
              disabled={!isValid || loading || Object.keys(errors).length > 0}
              type='submit'
              title='Add'
              data-automation="add-button"
              loading={loading}
            />
            {/*<PrimaryButton disabled={!isValid} title="Add" type='submit' />*/}
          </GeneralModal.ButtonArea>
        </form>
      </GeneralModal>
    </MuiPickersUtilsProvider >
  );
};

const useStyles = makeStyles((theme: ITheme) => ({
  cssOutlinedInput: {
    display: "flex",
    "&$cssFocused $notchedOutline": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
  cssFocused: {},
  notchedOutline: {
    border: "1px solid rgba(0, 0, 0, 0.12)",
  },
  fieldset: {
    marginTop: 4,
    padding: 0,
    border: 0,
  },
  fieldsetLegend: {
    paddingLeft: "14px",
  },
  prefix: {
    color: theme.palette.action.disabled,
    marginRight: 5,
    marginTop: 2
  },
  suffix: {
    color: theme.palette.action.disabled,
  },
}));